import z from "zod";
import { SourceMetadataListSchema } from "../source-metadata.schema.js";
export const GitHubSearchResultItemSchema = z.object({
    type: z.enum([
        "issues",
        "pull_requests",
        "repositories",
        "commits",
        "discussions",
    ]),
    id: z.string(),
    number: z.number().int().optional(),
    title: z.string(),
    url: z.string().url(),
    state: z.string().optional(),
    repository: z.string().optional(),
    updatedAt: z.string().nullable().optional(),
    excerpt: z.string().nullable().optional(),
});
export const GitHubIssueSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    title: z.string(),
    state: z.string(),
    url: z.string().url(),
    repository: z.string(),
    body: z.string().nullable(),
    author: z.string().nullable(),
    labels: z.array(z.string()),
    assignees: z.array(z.string()),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    commentsCount: z.number().int(),
});
export const GitHubPullRequestSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    title: z.string(),
    state: z.string(),
    url: z.string().url(),
    repository: z.string(),
    body: z.string().nullable(),
    author: z.string().nullable(),
    baseBranch: z.string().nullable(),
    headBranch: z.string().nullable(),
    merged: z.boolean(),
    draft: z.boolean(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const GitHubRepositorySchema = z.object({
    id: z.string(),
    name: z.string(),
    fullName: z.string(),
    url: z.string().url(),
    description: z.string().nullable(),
    defaultBranch: z.string().nullable(),
    isPrivate: z.boolean(),
    openIssuesCount: z.number().int(),
    updatedAt: z.string().nullable(),
});
export const GitHubSearchInputSchema = z.object({
    // Empty query is allowed: provider scopes via owner/repo/org + type qualifiers
    // (same pattern as confluence-search "show all").
    query: z.string().optional().default(""),
    owner: z.string().optional(),
    repo: z.string().optional(),
    type: z
        .enum(["issues", "pull_requests", "repositories", "commits", "discussions"])
        .optional(),
    maxResults: z.number().int().min(1).max(20).optional(),
});
export const GitHubSearchOutputSchema = z.object({
    source: z.literal("GitHub"),
    query: z.string(),
    count: z.number().int(),
    results: z.array(GitHubSearchResultItemSchema),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubListRepositoriesInputSchema = z.object({});
export const GitHubListRepositoriesOutputSchema = z.object({
    source: z.literal("GitHub"),
    count: z.number().int(),
    repositories: z.array(GitHubRepositorySchema),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubGetIssueInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    issueNumber: z.number().int().positive(),
});
export const GitHubGetIssueOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    issue: GitHubIssueSchema.nullable(),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubGetPullRequestInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    pullNumber: z.number().int().positive(),
});
export const GitHubGetPullRequestOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    pullRequest: GitHubPullRequestSchema.nullable(),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubGetRepositoryInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
});
export const GitHubGetRepositoryOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    repository: GitHubRepositorySchema.nullable(),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubCreateIssueInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    title: z.string().min(1),
    body: z.string().optional(),
    labels: z.array(z.string().min(1)).optional(),
});
export const GitHubCreateIssueOutputSchema = z.object({
    source: z.literal("GitHub"),
    result: z.object({
        ok: z.literal(true),
        issue: GitHubIssueSchema,
    }),
});
export const GitHubAddCommentInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    issueNumber: z.number().int().positive(),
    body: z.string().min(1),
});
export const GitHubAddCommentOutputSchema = z.object({
    source: z.literal("GitHub"),
    result: z.object({
        ok: z.literal(true),
        issueNumber: z.number().int(),
        commentUrl: z.string().url(),
    }),
});
export const GitHubUpdateIssueInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    issueNumber: z.number().int().positive(),
    title: z.string().min(1).optional(),
    body: z.string().optional(),
    state: z.enum(["open", "closed"]).optional(),
    labels: z.array(z.string().min(1)).optional(),
});
export const GitHubUpdateIssueOutputSchema = z.object({
    source: z.literal("GitHub"),
    result: z.object({
        ok: z.literal(true),
        issue: GitHubIssueSchema,
    }),
});
export const GitHubTransitionIssueInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    issueNumber: z.number().int().positive(),
    state: z.enum(["open", "closed"]),
    stateReason: z.enum(["completed", "not_planned"]).optional(),
});
export const GitHubTransitionIssueOutputSchema = z.object({
    source: z.literal("GitHub"),
    result: z.object({
        ok: z.literal(true),
        issue: GitHubIssueSchema,
    }),
});
export const GitHubCommentSchema = z.object({
    id: z.string(),
    kind: z.enum(["issue", "review"]),
    body: z.string(),
    author: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    url: z.string().url(),
});
export const GitHubGetCommentsInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    pullNumber: z.number().int().positive(),
    limit: z.number().int().min(1).max(50).optional(),
    includeReviewComments: z.boolean().optional(),
});
export const GitHubGetCommentsOutputSchema = z.object({
    source: z.literal("GitHub"),
    owner: z.string(),
    repo: z.string(),
    pullNumber: z.number().int(),
    comments: z.array(GitHubCommentSchema),
    sources: SourceMetadataListSchema.optional(),
});
export const GitHubFileOmitReasonSchema = z.enum([
    "not_found",
    "access_denied",
    "is_directory",
    "binary",
    "too_large",
    "truncated",
]);
export const GitHubGetFileContentInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    path: z.string().min(1),
    ref: z.string().min(1).optional(),
});
export const GitHubGetFileContentOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    owner: z.string(),
    repo: z.string(),
    path: z.string(),
    ref: z.string().nullable(),
    sha: z.string().nullable(),
    htmlUrl: z.string().url().nullable(),
    encoding: z.string().nullable(),
    size: z.number().int().nullable(),
    languageHint: z.string().nullable(),
    content: z.string().nullable(),
    lineCount: z.number().int().nullable().optional(),
    omitReason: GitHubFileOmitReasonSchema.nullable(),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubTreeEntrySchema = z.object({
    path: z.string().min(1),
    type: z.enum(["file", "dir"]),
    size: z.number().int().optional(),
});
export const GitHubGetTreeInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    path: z.string().min(1).optional(),
    ref: z.string().min(1).optional(),
    recursive: z.boolean().optional().default(true),
});
export const GitHubGetTreeOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    owner: z.string(),
    repo: z.string(),
    path: z.string().nullable(),
    ref: z.string().nullable(),
    htmlUrl: z.string().url().nullable(),
    truncated: z.boolean(),
    entries: z.array(GitHubTreeEntrySchema),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const GitHubDiffFileOmitReasonSchema = z.enum([
    "too_large",
    "binary",
    "unavailable",
]);
export const GitHubPullRequestDiffFileSchema = z.object({
    filename: z.string().min(1),
    status: z.string().min(1),
    additions: z.number().int(),
    deletions: z.number().int(),
    patch: z.string().nullable(),
    omitReason: GitHubDiffFileOmitReasonSchema.nullable(),
});
export const GitHubGetPullRequestDiffInputSchema = z.object({
    owner: z.string().min(1),
    repo: z.string().min(1),
    pullNumber: z.number().int().positive(),
});
export const GitHubGetPullRequestDiffOutputSchema = z.object({
    source: z.literal("GitHub"),
    found: z.boolean(),
    owner: z.string(),
    repo: z.string(),
    pullNumber: z.number().int(),
    title: z.string().nullable(),
    htmlUrl: z.string().url().nullable(),
    baseRef: z.string().nullable(),
    headRef: z.string().nullable(),
    truncated: z.boolean(),
    files: z.array(GitHubPullRequestDiffFileSchema),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
//# sourceMappingURL=github.schema.js.map